import type { Phase } from "../../../../common/types.ts";
import { helpers } from "./helpers.ts";
import i18n from "./i18n.ts";
import {
	parseMobileReturnState,
	type MobileReturnState,
} from "./mobilePageContext.ts";
import {
	mobileNavigationItems,
	resolveMobileNavigationRoot,
	resolveMobilePageMode,
	resolveMobileSection,
	type MarketPageID,
	type MobileNavigationContext,
	type MobileSection,
} from "./mobileNavigation.ts";

// N1: sessionStorage-backed navigation back stack. The stack holds the trail of
// ancestor sub-pages for the current view so a single global back control can
// climb ≥3 levels without depending on nested URL params (which truncate). The
// URL stays clean; the legacy `m_return` params survive only as an entry seed so
// existing deep links / news entries keep working.

export type MobileBackStackEntry = {
	url: string;
	label?: string;
	section: MobileSection;
};

export type MobileBackControlContext = {
	pageID: unknown;
	playerTid?: number | null;
	phase: Phase;
	hasOpenFreeAgency?: boolean;
	hasPendingNegotiations?: boolean;
	needsExpansionProtection?: boolean;
	lastMarketPageID?: MarketPageID;
};

export type MobileBackControl = {
	href: string;
	label: string;
};

const STORAGE_KEY = "bbgm.mobileBackStack";
export const MOBILE_BACK_STACK_MAX_DEPTH = 20;
const MAX_DEPTH = MOBILE_BACK_STACK_MAX_DEPTH;
const RETURN_INVALID_ORIGIN = "https://mobile-return.invalid";

const mobileSections = new Set<MobileSection>([
	"home",
	"team",
	"games",
	"market",
	"league",
]);

const isMobileSection = (value: unknown): value is MobileSection =>
	typeof value === "string" && mobileSections.has(value as MobileSection);

// ─── Storage + subscription plumbing (SSR-guarded) ────────────────────────────

let cache: MobileBackStackEntry[] | undefined;
const listeners = new Set<() => void>();
const emptySnapshot: MobileBackStackEntry[] = [];

const getSessionStorage = (): Storage | undefined => {
	if (typeof window === "undefined") {
		return undefined;
	}
	try {
		return window.sessionStorage ?? undefined;
	} catch {
		// Access to sessionStorage can throw (e.g. sandboxed iframes).
		return undefined;
	}
};

const isValidEntry = (value: unknown): value is MobileBackStackEntry => {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	const entry = value as Record<string, unknown>;
	return (
		typeof entry.url === "string" &&
		entry.url.startsWith("/") &&
		isMobileSection(entry.section) &&
		(entry.label === undefined || typeof entry.label === "string")
	);
};

const loadFromStorage = (): MobileBackStackEntry[] => {
	const storage = getSessionStorage();
	if (!storage) {
		return [];
	}
	try {
		const raw = storage.getItem(STORAGE_KEY);
		if (!raw) {
			return [];
		}
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) {
			return [];
		}
		return parsed.filter(isValidEntry).slice(-MAX_DEPTH);
	} catch {
		return [];
	}
};

const readCache = (): MobileBackStackEntry[] => {
	if (cache === undefined) {
		cache = loadFromStorage();
	}
	return cache;
};

const writeCache = (next: MobileBackStackEntry[]) => {
	const current = readCache();
	if (
		current.length === next.length &&
		current.every(
			(entry, index) =>
				entry.url === next[index]?.url &&
				entry.label === next[index]?.label &&
				entry.section === next[index]?.section,
		)
	) {
		// No change — avoid churning subscribers / storage.
		return;
	}

	cache = next;

	const storage = getSessionStorage();
	if (storage) {
		try {
			if (next.length === 0) {
				storage.removeItem(STORAGE_KEY);
			} else {
				storage.setItem(STORAGE_KEY, JSON.stringify(next));
			}
		} catch {
			// Ignore quota / access errors; the in-memory cache still works.
		}
	}

	for (const listener of listeners) {
		listener();
	}
};

export const subscribeMobileBackStack = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const getMobileBackStackSnapshot = (): MobileBackStackEntry[] =>
	readCache();

export const getMobileBackStackServerSnapshot = (): MobileBackStackEntry[] =>
	emptySnapshot;

export const readMobileBackStack = (): MobileBackStackEntry[] => [...readCache()];

export const peekMobileBackStack = (): MobileBackStackEntry | undefined => {
	const stack = readCache();
	return stack[stack.length - 1];
};

export const clearMobileBackStack = () => {
	writeCache([]);
};

// Test-only: drops the in-memory cache so the next read re-hydrates from storage.
export const resetMobileBackStackForTesting = () => {
	cache = undefined;
	writeCache([]);
	cache = undefined;
};

// ─── URL identity helpers ─────────────────────────────────────────────────────

const stripTrailingSlash = (path: string) =>
	path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;

// Two URLs point at the "same page" if their path + non-return query match. All
// `m_*` navigation-context params are dropped so a page reached with a return
// seed compares equal to the same page reached from the stack.
const pageIdentity = (url: string): string => {
	try {
		const parsed = new URL(url, RETURN_INVALID_ORIGIN);
		for (const key of [...parsed.searchParams.keys()]) {
			if (key.startsWith("m_")) {
				parsed.searchParams.delete(key);
			}
		}
		const query = parsed.searchParams.toString();
		return `${stripTrailingSlash(parsed.pathname)}${query ? `?${query}` : ""}`;
	} catch {
		return stripTrailingSlash(url);
	}
};

const samePage = (a: string, b: string) => pageIdentity(a) === pageIdentity(b);

const getLocationParts = () => {
	if (typeof window === "undefined") {
		return undefined;
	}
	const { pathname, search } = window.location;
	if (typeof pathname !== "string" || pathname.length === 0) {
		return undefined;
	}
	const safeSearch = typeof search === "string" ? search : "";
	return {
		pathname,
		search: safeSearch,
		url: `${pathname}${safeSearch}`,
	};
};

// ─── Section / root resolution ────────────────────────────────────────────────

const shouldPreserveSourceTab = (returnState: MobileReturnState) =>
	returnState.sourceSection !== undefined &&
	(returnState.archiveSeason !== undefined ||
		returnState.readonlyReason !== undefined ||
		returnState.sourcePage !== undefined);

const buildNavigationContext = (
	ctx: MobileBackControlContext,
	returnState: MobileReturnState,
): MobileNavigationContext => ({
	hasOpenFreeAgency: ctx.hasOpenFreeAgency,
	hasPendingNegotiations: ctx.hasPendingNegotiations,
	lastMarketPageID: ctx.lastMarketPageID,
	needsExpansionProtection: ctx.needsExpansionProtection,
	originSection: returnState.sourceSection,
	phase: ctx.phase,
	playerTid: ctx.playerTid ?? undefined,
	preserveOriginSection: shouldPreserveSourceTab(returnState),
});

const sectionRootTarget = (
	section: MobileSection,
	navContext: MobileNavigationContext,
): MobileBackControl => {
	const segments = resolveMobileNavigationRoot(section, navContext);
	const item = mobileNavigationItems.find((entry) => entry.key === section);
	const label = item
		? i18n.t(item.labelKey, { ns: "ui", defaultValue: item.defaultLabel })
		: section;
	return {
		href: helpers.leagueUrl(segments),
		label,
	};
};

const isSectionRoot = (
	section: MobileSection,
	navContext: MobileNavigationContext,
	currentPath: string,
) => {
	const rootHref = helpers.leagueUrl(
		resolveMobileNavigationRoot(section, navContext),
	);
	return stripTrailingSlash(currentPath) === stripTrailingSlash(rootHref);
};

type ResolvedContext = {
	navContext: MobileNavigationContext;
	returnState: MobileReturnState;
	section: MobileSection | undefined;
};

const resolveContext = (
	ctx: MobileBackControlContext,
	search: string,
): ResolvedContext => {
	const returnState = parseMobileReturnState(search);
	const navContext = buildNavigationContext(ctx, returnState);
	const section = resolveMobileSection(ctx.pageID, navContext);
	return {
		navContext,
		returnState,
		section: isMobileSection(section) ? section : undefined,
	};
};

// ─── Pure reconciliation core (unit-tested) ───────────────────────────────────

export type MobileBackStackReconcileInput = {
	stack: readonly MobileBackStackEntry[];
	currentUrl: string;
	isSectionRoot: boolean;
	section: MobileSection;
	seed?: {
		href: string;
		label?: string;
		section?: MobileSection;
	};
};

// Computes the next stack for a navigation. The current page is never itself on
// the stack — the stack holds only its ancestors, top === immediate parent.
export const reconcileMobileBackStack = ({
	stack,
	currentUrl,
	isSectionRoot: atSectionRoot,
	section,
	seed,
}: MobileBackStackReconcileInput): MobileBackStackEntry[] => {
	// Section root → nothing to go back to.
	if (atSectionRoot) {
		return [];
	}

	// Case 1: navigated back/up to a page already in the trail. Truncate to it
	// (drop it + everything deeper); its parent becomes the new top.
	const existingIndex = stack.findIndex((entry) => samePage(entry.url, currentUrl));
	if (existingIndex !== -1) {
		return stack.slice(0, existingIndex);
	}

	// Case 2: a fresh sub-page carrying a return seed (m_return). Push the seeded
	// parent so back climbs to it.
	if (seed?.href !== undefined) {
		const parentSection = isMobileSection(seed.section) ? seed.section : section;
		const entry: MobileBackStackEntry = {
			url: seed.href,
			label: seed.label,
			section: parentSection,
		};
		const top = stack[stack.length - 1];
		if (top !== undefined && samePage(top.url, entry.url)) {
			return [...stack];
		}
		const next = [...stack, entry];
		while (next.length > MAX_DEPTH) {
			next.shift();
		}
		return next;
	}

	// Case 3: a fresh sub-page with no seed (bare link / cold deep link). The
	// trail is unknown, so reset — the back control falls back to the section
	// root instead of pointing at an unrelated page.
	return [];
};

// ─── Public: reconcile stack on navigation ────────────────────────────────────

// Runs once per navigation (keyed on the URL) to keep the persistent stack in
// sync with the current location.
export const syncMobileBackStack = (ctx: MobileBackControlContext) => {
	const loc = getLocationParts();
	if (!loc) {
		return;
	}

	// Fullscreen (liveGame) / out-of-league pages are not part of the sub-page
	// trail — leave the stack untouched so returning lands where the user was.
	if (resolveMobilePageMode(ctx.pageID) !== "standard") {
		return;
	}

	const { navContext, returnState, section } = resolveContext(ctx, loc.search);
	if (section === undefined) {
		// Unknown page ownership — don't corrupt the trail.
		return;
	}

	writeCache(
		reconcileMobileBackStack({
			currentUrl: loc.url,
			isSectionRoot: isSectionRoot(section, navContext, loc.pathname),
			section,
			seed:
				returnState.returnHref !== undefined
					? {
							href: returnState.returnHref,
							label: returnState.returnLabel,
							section: isMobileSection(returnState.sourceSection)
								? returnState.sourceSection
								: undefined,
						}
					: undefined,
			stack: readCache(),
		}),
	);
};

// ─── Public: resolve the back control for the current page ────────────────────

export const getMobileBackControl = (
	ctx: MobileBackControlContext,
): MobileBackControl | undefined => {
	const loc = getLocationParts();
	if (!loc) {
		return undefined;
	}

	if (resolveMobilePageMode(ctx.pageID) !== "standard") {
		return undefined;
	}

	const { navContext, section } = resolveContext(ctx, loc.search);
	if (section === undefined) {
		return undefined;
	}

	if (isSectionRoot(section, navContext, loc.pathname)) {
		return undefined;
	}

	const top = peekMobileBackStack();
	if (top !== undefined) {
		return {
			href: top.url,
			label: top.label ?? sectionRootTarget(top.section, navContext).label,
		};
	}

	return sectionRootTarget(section, navContext);
};
